import { call } from '@advanza/api'
import { invalidateNotes } from 'actions/notes'
import { fetchProvider } from 'actions/providers'
import { useAppDispatch } from 'hooks/hooks'
import { useState } from 'react'
import { isFieldsRejection } from 'utils/getRejectionMessage'

export interface NewFreeLeadsEntity {
    comment?: string
    nr_leads?: number
    expiration?: string | null
    after_payed?: boolean
    enable_after_nr_leads_payed?: number
    free_lead_type?: string | null
}

/** Per-field messages of a validation rejection, keyed by the rule that failed. */
interface FreeLeadsFieldErrors {
    nr_leads?: Record<string, string>
    comment?: Record<string, string>
    enable_after_nr_leads_payed?: Record<string, string>
    expiration?: Record<string, string>
    free_lead_type?: Record<string, string> | string[]
}

const useAddFreeLeads = (providerId: number, initialNewEntity: NewFreeLeadsEntity) => {
    const dispatch = useAppDispatch()
    const [newEntity, setNewEntity] = useState<NewFreeLeadsEntity>(initialNewEntity)
    const [errors, setErrors] = useState<FreeLeadsFieldErrors | null>(null)
    const [isSaving, setIsSaving] = useState(false)

    const onChangeNewEntity = (changes: NewFreeLeadsEntity) =>
        setNewEntity((currentEntity) => ({ ...currentEntity, ...changes }))

    const onSave = () => {
        setErrors(null)
        setIsSaving(true)

        return call('office/providers/add-free-leads/', {
            json: {
                service_provider_id: providerId,
                ...newEntity,
            },
        })
            .then(
                () => {
                    dispatch(fetchProvider(providerId, true))
                    dispatch(invalidateNotes())
                },
                (rejection) => {
                    // Only the fields with an input of their own can show their message there, so
                    // the rejection is handed on for the caller to report whatever is left.
                    setErrors(isFieldsRejection<FreeLeadsFieldErrors>(rejection) ? rejection : null)

                    return Promise.reject(rejection)
                },
            )
            .finally(() => {
                setIsSaving(false)
            })
    }

    return {
        newEntity,
        onChangeNewEntity,
        isSaving,
        onSave,
        errors,
    }
}

export default useAddFreeLeads
